/**
 * AURA Audio Utilities
 * Synthesizes notification sounds using the Web Audio API.
 * No external assets required -- tones are generated at runtime.
 */

import { Logger } from './logger';

const logger = new Logger('Audio');

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  try {
    if (!audioCtx) {
      audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
    }
    return audioCtx;
  } catch (err) {
    logger.warn('Web Audio API unavailable', { error: err });
    return null;
  }
}

/**
 * Play a short "ding" notification tone.
 * Uses two overlapping sine waves to create a pleasant chime.
 */
export function playActivationDing(): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    const now = ctx.currentTime;
    const duration = 0.3;

    const gainNode = ctx.createGain();
    gainNode.connect(ctx.destination);
    gainNode.gain.setValueAtTime(0.25, now);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + duration);

    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(880, now); // A5
    osc1.connect(gainNode);
    osc1.start(now);
    osc1.stop(now + duration);

    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1318.5, now + 0.08); // E6, slightly delayed
    osc2.connect(gainNode);
    osc2.start(now + 0.08);
    osc2.stop(now + duration);

    logger.debug('Played activation ding');
  } catch (err) {
    logger.warn('Failed to play activation ding', { error: err });
  }
}

/**
 * Play a softer descending tone for deactivation.
 */
export function playDeactivationTone(): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    const now = ctx.currentTime;
    const duration = 0.2;

    const gainNode = ctx.createGain();
    gainNode.connect(ctx.destination);
    gainNode.gain.setValueAtTime(0.15, now);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + duration);

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(660, now); // E5
    osc.frequency.exponentialRampToValueAtTime(440, now + duration); // descend to A4
    osc.connect(gainNode);
    osc.start(now);
    osc.stop(now + duration);

    logger.debug('Played deactivation tone');
  } catch (err) {
    logger.warn('Failed to play deactivation tone', { error: err });
  }
}
